/**
 * Main schedule chart container.
 * Provides project filter, grouping controls, legend, and LBS chart.
 */

import { useEffect, useRef, useState, useMemo } from "react";
import { useProjects } from "@/hooks/useProjects";
import { useScheduleData, type GroupBy } from "@/hooks/useScheduleData";
import { filterVisibleRows, getDefaultExpandedIds, buildCollapseRemap, type Flowline, type FlowlinePoint } from "./chart-utils";
import { LBSChart } from "./LBSChart";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";

const ROW_HEIGHT = 28;

export function ScheduleChart() {
  const { projects, loading: projectsLoading } = useProjects();
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [groupBy, setGroupBy] = useState<GroupBy>("procurementPackage");
  const [selectedFlowline, setSelectedFlowline] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const selectedProjectId = projectFilter || null;
  const { locationRows: allRows, flowlines, dateRange, loading: dataLoading } =
    useScheduleData(selectedProjectId, groupBy);

  const loading = projectsLoading || dataLoading;

  useEffect(() => {
    if (allRows.length > 0) {
      setExpandedIds(getDefaultExpandedIds(allRows));
    }
  }, [allRows]);

  const visibleRows = useMemo(
    () => filterVisibleRows(allRows, expandedIds, ROW_HEIGHT),
    [allRows, expandedIds],
  );

  const collapseRemap = useMemo(
    () => buildCollapseRemap(allRows, expandedIds),
    [allRows, expandedIds],
  );

  const remappedFlowlines = useMemo((): Flowline[] => {
    if (collapseRemap.size === 0) return flowlines;

    return flowlines.map((fl) => {
      const remappedPoints: FlowlinePoint[] = fl.points.map((pt) => ({
        ...pt,
        locationId: collapseRemap.get(pt.locationId) || pt.locationId,
      }));

      const byLocation = new Map<string, FlowlinePoint>();
      for (const pt of remappedPoints) {
        const existing = byLocation.get(pt.locationId);
        if (existing) {
          existing.startDate = new Date(Math.min(existing.startDate.getTime(), pt.startDate.getTime()));
          existing.endDate = new Date(Math.max(existing.endDate.getTime(), pt.endDate.getTime()));
        } else {
          byLocation.set(pt.locationId, { ...pt });
        }
      }

      return {
        ...fl,
        points: Array.from(byLocation.values()),
      };
    });
  }, [flowlines, collapseRemap]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Double-click: expand or collapse ALL descendants of a location
  const toggleExpandAll = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      const isExpanded = next.has(id);

      // Find all descendant IDs that have children
      const idx = allRows.findIndex((r) => r.id === id);
      if (idx === -1) return prev;
      const parentDepth = allRows[idx].depth;
      const descendantIds: string[] = [id];
      for (let i = idx + 1; i < allRows.length; i++) {
        if (allRows[i].depth <= parentDepth) break;
        if (allRows[i].hasChildren) descendantIds.push(allRows[i].id);
      }

      if (isExpanded) {
        // Collapse all
        for (const did of descendantIds) next.delete(did);
      } else {
        // Expand all
        for (const did of descendantIds) next.add(did);
      }
      return next;
    });
  };

  const hasChart = visibleRows.length > 0 || allRows.length > 0;

  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [chartAreaHeight, setChartAreaHeight] = useState(0);
  useEffect(() => {
    if (!chartAreaRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setChartAreaHeight(entry.contentRect.height);
    });
    ro.observe(chartAreaRef.current);
    return () => ro.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="-m-6 flex flex-col h-[calc(100dvh-0px)]">
        <div className="flex items-center gap-3 px-6 py-3 border-b">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-40" />
        </div>
        <div className="flex-1 bg-muted/20" />
      </div>
    );
  }

  return (
    <div className="-m-6 flex flex-col h-[calc(100dvh-0px)]">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b bg-background shrink-0">
        <h1 className="text-lg font-semibold mr-2">Aikataulu</h1>

        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-[220px] h-8 text-sm">
            <SelectValue placeholder="Valitse hanke..." />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedProjectId && (
          <div className="flex rounded-md border">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "rounded-none border-r h-8 text-xs",
                groupBy === "procurementPackage" && "bg-accent",
              )}
              onClick={() => setGroupBy("procurementPackage")}
            >
              Hankintapaketit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "rounded-none h-8 text-xs",
                groupBy === "section" && "bg-accent",
              )}
              onClick={() => setGroupBy("section")}
            >
              Työvaiheet
            </Button>
          </div>
        )}

        {/* Legend inline in toolbar */}
        {remappedFlowlines.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {remappedFlowlines.map((fl) => (
              <button
                key={fl.id}
                className={cn(
                  "flex items-center gap-1 px-1.5 py-0.5 rounded text-xs transition-opacity",
                  selectedFlowline !== null && selectedFlowline !== fl.id && "opacity-30",
                )}
                onClick={() =>
                  setSelectedFlowline(
                    selectedFlowline === fl.id ? null : fl.id,
                  )
                }
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: fl.color }}
                />
                {fl.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chart area — fills remaining viewport */}
      <div ref={chartAreaRef} className="flex-1 min-h-0 overflow-auto">
        {!hasChart ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <CalendarIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
              {selectedProjectId ? (
                <>
                  <p className="font-medium text-sm">Sijainteja ei löytynyt</p>
                  <p className="text-xs mt-1">
                    Lisää hankkeelle sijainteja nähdäksesi aikataulun
                  </p>
                </>
              ) : (
                <p className="text-sm">Valitse hanke nähdäksesi aikataulun</p>
              )}
            </div>
          </div>
        ) : (
          <LBSChart
            locationRows={visibleRows}
            flowlines={remappedFlowlines}
            dateRange={dateRange}
            selectedFlowline={selectedFlowline}
            onSelectFlowline={setSelectedFlowline}
            expandedIds={expandedIds}
            onToggleExpand={toggleExpand}
            onToggleExpandAll={toggleExpandAll}
            minHeight={chartAreaHeight}
          />
        )}
      </div>
    </div>
  );
}
